import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alerts,
  Button,
  Container,
  FormActions,
  FormNotice,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  Header,
  Modal,
  PageContent,
  StatusBadge,
} from 'components';
import { checkPermissions, globalPropTypes } from 'helpers';
import { formatDate, formatTime, toApiDate } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadYcReportCreator,
  saveYcReportCreator,
  removeYcReportCreator,
} from 'store/actionsCreators';
import ReportBody from './ReportBody';
import StatusHistory from './StatusHistory';
import buildFormState from './reportState';
import { SavedNote } from '../Reports.styled';

const breadcrumbs = (mode) => [
  {
    title: 'navigation.breadcrumbs.yc.reports',
    url: '/yc/reports',
  },
  {
    title:
      mode === 'view'
        ? 'navigation.breadcrumbs.yc.readReport'
        : 'navigation.breadcrumbs.yc.addReport',
    url: '',
  },
];

/**
 * The yearly report of a Young Crew: the four groups it consists of, and the
 * two ways out of it - saved as a draft, or sent to the YCMB for review.
 *
 * The same screen renders the read only view of a report that was decided on,
 * with every input disabled, which is why the mode decides what is rendered
 * rather than a second copy of the form.
 */
const ReportForm = ({
  mode,
  data,
  errors,
  savedAt,
  userPermissions,
  loadReport,
  saveReport,
  clearReport,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [form, setForm] = useState(null);
  const [leaving, setLeaving] = useState(false);
  // New events have no id to be addressed by, so the form gives them a key that
  // ties their inputs and their uploads together until they are stored.
  const nextKey = useRef(0);
  // The card of each event, so a freshly added one can be brought into view.
  const cards = useRef({});

  const neededRole = mode === 'view' ? 'yc_reports_view' : 'yc_reports_send';
  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: [neededRole] }, userPermissions || []),
    '/yc',
    'page.yc.reports.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadReport({ id: id ? Number(id) : null });

    return () => clearReport();
  }, [id, allowed]);

  useEffect(() => {
    if (data) setForm(buildFormState(data));
  }, [data]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  if (!data || !form) {
    return (
      <>
        <Header breadcrumbs={breadcrumbs(mode)} title={t('page.yc.reports.form.title')} />
        <PageContent />
      </>
    );
  }

  const { report } = data;
  const readOnly = mode === 'view' || !data.editable;
  const status = report ? report.status : null;

  const patch = (values) => setForm((previous) => ({ ...previous, ...values }));

  const patchEvent = (key, values) =>
    setForm((previous) => ({
      ...previous,
      events: previous.events.map((event) => (event.key === key ? { ...event, ...values } : event)),
    }));

  const registerCard = (key) => (node) => {
    cards.current[key] = node;
  };

  const addEvent = () => {
    nextKey.current += 1;
    const key = `new-${nextKey.current}`;

    setForm((previous) => ({
      ...previous,
      events: [
        ...previous.events,
        {
          key,
          id: null,
          // Only a card added here eases in; the ones a saved report came with
          // are already on screen and should not animate on every render.
          isNew: true,
          registered: true,
          eventId: null,
          name: null,
          date: null,
          modeOfDelivery: null,
          participants: null,
          proof: [],
          newProof: [],
          removedProofFiles: [],
        },
      ],
    }));

    // The new card is below the fold once a few events are listed, so the page
    // follows it down instead of leaving the user to find what just appeared.
    window.requestAnimationFrame(() => {
      const card = cards.current[key];

      if (card && card.scrollIntoView) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  const removeEvent = (key) => {
    delete cards.current[key];

    setForm((previous) => ({
      ...previous,
      events: previous.events.filter((event) => event.key !== key),
    }));
  };

  /**
   * The proof of one event, changed off the event as it is right now: picking
   * several files in a row queues several of these before React re-renders, so
   * each has to read the list the one before it left behind.
   */
  const mapEvent = (key, map) =>
    setForm((previous) => ({
      ...previous,
      events: previous.events.map((event) => (event.key === key ? map(event) : event)),
    }));

  const addProof = (key, file) =>
    mapEvent(key, (event) => ({ ...event, newProof: [...event.newProof, file] }));

  const removeStoredProof = (key, fileId) =>
    mapEvent(key, (event) => ({
      ...event,
      proof: event.proof.filter((file) => file.id !== fileId),
      removedProofFiles: [...event.removedProofFiles, fileId],
    }));

  const removePendingProof = (key, index) =>
    mapEvent(key, (event) => ({
      ...event,
      newProof: event.newProof.filter((file, i) => i !== index),
    }));

  /**
   * Picking several files at once queues one of these per file, so the list is
   * read off the state that is being updated rather than off this render.
   */
  const addDocument = (group, file) =>
    setForm((previous) => ({ ...previous, [group]: [...previous[group], file] }));

  const removeStoredDocument = (group, fileId) =>
    setForm((previous) => ({
      ...previous,
      [group]: previous[group].filter((file) => file.id !== fileId),
      removedFiles: [...previous.removedFiles, fileId],
    }));

  const removePendingDocument = (group, index) =>
    setForm((previous) => ({
      ...previous,
      [group]: previous[group].filter((file, i) => i !== index),
    }));

  const submitForm = (submit) => {
    saveReport(
      {
        reportId: report ? report.id : null,
        submit,
        participatedInGlobalEvents: form.participatedInGlobalEvents,
        globalEvents: form.globalEvents,
        removedFiles: form.removedFiles,
        yearlyPlanFiles: form.yearlyPlanNew,
        yearlyBudgetFiles: form.yearlyBudgetNew,
        events: form.events.map((event) => ({
          key: event.key,
          id: event.id,
          eventId: event.registered ? event.eventId : null,
          name: event.registered ? null : event.name,
          date: event.registered || !event.date ? null : toApiDate(event.date),
          modeOfDelivery: event.registered ? null : event.modeOfDelivery,
          participants: event.participants,
          removedProofFiles: event.removedProofFiles,
          newProofFiles: event.newProof,
        })),
      },
      history,
    );
  };

  const leave = () => history.push('/yc/reports');

  const renderActions = () => {
    if (readOnly) {
      return (
        <FormActions>
          <Button type="button" secondary style={{ width: 'auto' }} onClick={leave}>
            {t('page.yc.reports.form.back')}
          </Button>
        </FormActions>
      );
    }

    return (
      <FormActions>
        {status !== 'SUBMITTED' ? (
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => submitForm(false)}
          >
            {t('page.yc.reports.form.saveDraft')}
          </Button>
        ) : null}
        <Button type="button" style={{ width: 'auto' }} onClick={() => submitForm(true)}>
          {status === 'SUBMITTED'
            ? t('page.yc.reports.form.saveChanges')
            : t('page.yc.reports.form.submit')}
        </Button>
        {/* Leaving throws away everything typed since the last save, so it is
            asked about rather than done on the first click. */}
        <Button type="button" secondary style={{ width: 'auto' }} onClick={() => setLeaving(true)}>
          {t('global.cancelButton')}
        </Button>
        {savedAt ? (
          <SavedNote>
            {t('page.yc.reports.form.draftSavedAt', { time: formatTime(savedAt) })}
          </SavedNote>
        ) : null}
      </FormActions>
    );
  };

  return (
    <>
      <Header breadcrumbs={breadcrumbs(mode)} title={t('page.yc.reports.form.title')} />
      <PageContent>
        <Container>
          <Alerts type="yc" />

          <FormSummary>
            <div>
              <FormSummaryHeading>
                {t('page.yc.reports.form.heading', { year: data.year })}
              </FormSummaryHeading>
              {data.schedule ? (
                <FormSummaryMeta>
                  {t('page.yc.reports.openPeriod', {
                    year: data.schedule.year,
                    date: formatDate(data.schedule.reportEnd),
                  })}
                </FormSummaryMeta>
              ) : null}
            </div>
            {report ? <StatusBadge status={report.status}>{report.statusLabel}</StatusBadge> : null}
          </FormSummary>

          {data.rejectionReason ? (
            <FormNotice type="error">
              {t('page.yc.reports.form.reason', { reason: data.rejectionReason })}
            </FormNotice>
          ) : null}
          {!data.editable && mode !== 'view' ? (
            <FormNotice type="warning">{t('page.yc.reports.form.closed')}</FormNotice>
          ) : null}

          <form>
            <ReportBody
              data={data}
              form={form}
              errors={errors}
              readOnly={readOnly}
              handlers={{
                addDocument,
                removeStoredDocument,
                removePendingDocument,
                patchEvent,
                addEvent,
                removeEvent,
                addProof,
                removeStoredProof,
                removePendingProof,
                registerCard,
                setParticipated: (value) => patch({ participatedInGlobalEvents: value }),
                setGlobalEvents: (value) => patch({ globalEvents: value }),
              }}
            />

            {renderActions()}

            {report ? <StatusHistory items={data.statusHistory} /> : null}
          </form>

          <Modal
            heading={t('page.yc.reports.form.leaveConfirm.heading')}
            description={t('page.yc.reports.form.leaveConfirm.description')}
            isOpen={leaving}
            confirm={leave}
            cancel={() => setLeaving(false)}
            onRequestClose={() => setLeaving(false)}
          />
        </Container>
      </PageContent>
    </>
  );
};

ReportForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit', 'view']).isRequired,
  data: PropTypes.shape({
    year: PropTypes.number,
    schedule: PropTypes.shape({
      year: PropTypes.number,
      reportEnd: PropTypes.string,
    }),
    report: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
    }),
    yearlyPlan: PropTypes.arrayOf(PropTypes.shape({})),
    yearlyBudget: PropTypes.arrayOf(PropTypes.shape({})),
    events: PropTypes.arrayOf(PropTypes.shape({})),
    participatedInGlobalEvents: PropTypes.bool,
    globalEvents: PropTypes.arrayOf(PropTypes.string),
    globalEventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    registeredEventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    modeOfDeliveryOptions: PropTypes.arrayOf(PropTypes.shape({})),
    allowedExtensions: PropTypes.shape({
      document: PropTypes.arrayOf(PropTypes.string),
      proof: PropTypes.arrayOf(PropTypes.string),
    }),
    maxFileSize: PropTypes.number,
    editable: PropTypes.bool,
    rejectionReason: PropTypes.string,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  errors: PropTypes.objectOf(PropTypes.string),
  savedAt: PropTypes.string,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loadReport: PropTypes.func.isRequired,
  saveReport: PropTypes.func.isRequired,
  clearReport: PropTypes.func.isRequired,
};

ReportForm.defaultProps = {
  data: null,
  errors: {},
  savedAt: null,
};

const mapStateToProps = ({ yc, user }) => ({
  data: yc.reports.form.data,
  errors: yc.reports.form.errors,
  savedAt: yc.reports.form.savedAt,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadReport: (payload) => dispatch(loadYcReportCreator(payload)),
  saveReport: (payload, history) => dispatch(saveYcReportCreator(payload, history)),
  clearReport: () => dispatch(removeYcReportCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportForm);
